
import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { LibraryModel } from '@/hooks/useLibraryModels'
import { activeSection, browseCatalog, committedKey, tabAfterKey, tabIntent, type BrowseItem, type RouteTab } from '@/lib/local-cloud-browse'

export function usesRouteTabs(models: { route?: string }[]) {
  const routes = new Set(models.map(model => model.route))
  return routes.has('local') && routes.has('cloud')
}

export function LibraryRouteMenu({ models, value, query, onQuery, onSelect, onBrowse }: {
  models: LibraryModel[]
  value: string
  query: string
  onQuery: (query: string) => void
  onSelect: (modelId: string) => void
  onBrowse: () => void
}) {
  const items: BrowseItem[] = models.flatMap(model => model.route === 'local' || model.route === 'cloud' ? [{
    key: model.modelId, label: model.modelName, route: model.route, selected: model.modelId === value, group: model.brandName ?? undefined, title: model.modelName,
  }] : [])
  const catalog = browseCatalog(items, query, ['local', 'cloud'])
  const listRef = React.useRef<HTMLDivElement>(null)
  const sectionRef = React.useRef<Partial<Record<RouteTab, HTMLElement | null>>>({})
  const [active, setActive] = React.useState<RouteTab>(catalog.sections[0]?.id ?? 'local')
  const offsets = () => catalog.sections.map(section => ({ id: section.id, top: sectionRef.current[section.id]?.offsetTop ?? 0 }))
  const show = (tab: RouteTab) => {
    const intent = tabIntent(tab, offsets(), catalog.selectedKey)
    listRef.current?.scrollTo({ top: intent.scrollTop })
    setActive(tab)
    void intent.selectedKey
  }
  return (
    <div className="flex max-h-72 min-h-0 w-full flex-col">
      <input value={query} onChange={event => onQuery(event.target.value)} placeholder="Search models..." aria-label="Search local and cloud models" className="w-full border-b border-border bg-transparent px-3 py-2 text-xs focus:outline-none" />
      <div role="tablist" aria-label="Model source" className="flex border-b border-border">
        {catalog.sections.map(section => (
          <button key={section.id} type="button" role="tab" aria-selected={active === section.id} aria-controls={`library-section-${section.id}`}
            onClick={() => show(section.id)}
            onKeyDown={event => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
              event.preventDefault()
              show(tabAfterKey(section.id, event.key, catalog.sections.map(item => item.id)))
            }}
            className={cn('flex-1 py-1.5 text-xs', active === section.id ? 'text-foreground' : 'text-muted-foreground')}
          >{section.label}</button>
        ))}
      </div>
      <div className="sr-only" aria-live="polite">{active === 'cloud' ? 'Cloud' : 'Local'}</div>
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1" onScroll={event => setActive(activeSection(event.currentTarget.scrollTop, offsets()))}>
        {catalog.sections.map(section => (
          <section key={section.id} id={`library-section-${section.id}`} ref={node => { sectionRef.current[section.id] = node }}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">{section.label}</div>
            {section.items.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">{section.empty}</p>}
            {section.items.map(item => (
              <button key={item.key} type="button" role="option" aria-selected={item.selected} className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm"
                onClick={() => { const key = committedKey(item, true); if (key) onSelect(key) }}>
                <Check className={cn('h-4 w-4 shrink-0', item.selected ? 'opacity-100' : 'opacity-0')} />
                <span className="truncate">{item.label}</span>
              </button>
            ))}
            {section.id === 'local' && section.items.length === 0 && (
              <button type="button" className="px-2 py-1 text-xs text-primary underline" onClick={onBrowse}>Browse Library</button>
            )}
          </section>
        ))}
      </div>
    </div>
  )
}

export interface LibraryModelPickerProps {
  models: LibraryModel[]
  value: string
  onValueChange: (modelId: string, brandSlug: string | null) => void
  isLoading?: boolean
  isEmpty?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Single combobox for selecting a model from the user's library.
 * Groups models by brand as section headers.
 */
export function LibraryModelPicker({
  models,
  value,
  onValueChange,
  isLoading,
  isEmpty,
  disabled,
  className,
}: LibraryModelPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const selectedModel = models.find((m) => m.modelId === value)

  // Filter by search query
  const filteredModels = React.useMemo(() => {
    if (!searchQuery) return models
    const query = searchQuery.toLowerCase()
    return models.filter((m) => m.modelName.toLowerCase().includes(query))
  }, [models, searchQuery])

  // Group by brand
  const groups = React.useMemo(() => {
    const map = new Map<string, LibraryModel[]>()
    for (const model of filteredModels) {
      const key = model.brandName ?? 'Other'
      const list = map.get(key)
      if (list) {
        list.push(model)
      } else {
        map.set(key, [model])
      }
    }
    return map
  }, [filteredModels])

  const handleSelect = React.useCallback(
    (modelId: string) => {
      const model = models.find((m) => m.modelId === modelId)
      onValueChange(modelId, model?.brandSlug ?? null)
      setOpen(false)
      setSearchQuery('')
    },
    [models, onValueChange]
  )

  const placeholder = isLoading ? 'Loading...' : 'Select model...'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label="Model"
          aria-expanded={open}
          className={cn('w-[180px] min-w-0 max-w-full h-8 justify-between', className)}
          disabled={disabled || isLoading || isEmpty}
        >
          <span className="truncate">
            {isEmpty
              ? 'No models'
              : selectedModel
                ? selectedModel.modelName
                : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="end">
        {usesRouteTabs(models) ? (
          <LibraryRouteMenu models={models} value={value} query={searchQuery} onQuery={setSearchQuery} onSelect={handleSelect}
            onBrowse={() => document.dispatchEvent(new CustomEvent('genie:open-library'))} />
        ) : (
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search models..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="no-focus"
          />
          <CommandList>
            {isEmpty ? (
              <button
                onClick={() => document.dispatchEvent(new CustomEvent('genie:open-library'))}
                className="w-full py-6 text-center cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <p className="text-sm text-muted-foreground">
                  No models in your library
                </p>
                <p className="mt-1 text-xs text-primary hover:underline">
                  Browse Library
                </p>
              </button>
            ) : (
              <>
                <CommandEmpty>No models found.</CommandEmpty>
                {Array.from(groups.entries()).map(([brandName, brandModels]) => (
                  <CommandGroup key={brandName} heading={brandName}>
                    {brandModels.map((model) => (
                      <CommandItem
                        key={model.modelId}
                        value={model.modelId}
                        onSelect={handleSelect}
                        className="flex items-center gap-2 py-2"
                      >
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            value === model.modelId ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span className="text-sm truncate">{model.modelName}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </>
            )}
          </CommandList>
        </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}
